import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline'
import i18n from 'i18next'
import React, { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import api from 'src/api'
import {
  FacetedListOffenseResponse,
  PolicyName,
  OffenseAggregation,
  Offense,
  Policy_KindEnum,
  Favorite_PageIdEnum,
} from 'src/api/gen/api'
import useDeepCompareEffect from 'src/commons/hooks/useDeepCompareEffect'
import usePolicyKindsExceptFeeRates from 'src/commons/hooks/usePolicyKindsExceptFeeRates'
import useSafeState from 'src/commons/hooks/useSafeState'
import { useURLAndStorageState } from 'src/commons/hooks/useURLAndStorageState'
import { userHasPermission } from 'src/commons/permissions'
import { getTemporalRange, ITemporalRange, ZonedDateTimeToJSON } from 'src/commons/temporalHelpers'
import { useOnboardingJoyride } from 'src/Joyride/OnboardingJoyride/OnBoardingJoyrideWrapper'
import { useProviders } from 'src/layout/hooks'
import { FilteringCard } from 'src/muiv5/components/commons/Cards/FilteringCard'

import ViolationsListFilterPanel, { IViolationListFilters } from './ListFilterPanel'
import ViolationsTable from './Table'

export function getInitialFilters(policyKinds: Policy_KindEnum[]): IViolationListFilters {
  const range: ITemporalRange = getTemporalRange({ months: 3 }, true)
  return {
    dateRange: range,
    eventTypes: [],
    include_abnormals: false,
    policyKind: policyKinds,
    policyId: [],
    provider: [],
    status: [],
    offenseStatus: [],
    vin: '',
  }
}

const Violations = () => {
  const { t } = useTranslation()

  const { activeProviders } = useProviders()

  const [arePoliciesLoading, setArePoliciesLoading] = useSafeState(true)
  const [areViolationsLoading, setAreViolationsLoading] = useSafeState(true)
  const [policies, setPolicies] = useSafeState<PolicyName[]>([])
  const [violations, setViolations] = useSafeState<Offense[]>([])
  const [violationsCount, setViolationsCount] = useSafeState<number>(0)
  const [violationsFacets, setViolationsFacets] = useSafeState<OffenseAggregation>({
    event_types: {},
    policy_ids: {},
    policy_types: {},
    providers: {},
    status: {},
    total: 0,
  })
  const policyKinds = usePolicyKindsExceptFeeRates().map((kind) => kind.id)
  const defaultFilters = useMemo(() => getInitialFilters(policyKinds), [policyKinds])
  const [filters, setFilters] = useURLAndStorageState(Favorite_PageIdEnum.policies_violations, defaultFilters)
  const displayedFiltersRef = useRef<IViolationListFilters>()
  const { resumeJoyride } = useOnboardingJoyride()

  useEffect(() => {
    setArePoliciesLoading(true)

    api.policies
      .policiesNames({ kind: policyKinds })
      .then(setPolicies)
      .catch(console.error)
      .finally(() => {
        setArePoliciesLoading(false)
      })
  }, [])

  const fetchViolations = async function (signal: AbortSignal) {
    setAreViolationsLoading(true)
    try {
      await api.offenses
        .offenseList({
          include_abnormals: filters.include_abnormals,
          end_date_after: filters.dateRange.from ? ZonedDateTimeToJSON(filters.dateRange.from) : undefined,
          policy_ids: filters.policyId,
          policy_types: filters.policyKind,
          provider_ids: filters.provider,
          start_date_before: filters.dateRange.to ? ZonedDateTimeToJSON(filters.dateRange.to) : undefined,
          vin: filters.vin,
          status: filters.status,
          event_types: filters.eventTypes,
          offense_status: filters.offenseStatus,
          options: { signal },
        })
        .then((response: FacetedListOffenseResponse) => {
          setViolations(response.results)
          setViolationsCount(response.aggregations.total)
          setViolationsFacets(response.aggregations)
        })
    } catch (error) {
      if (!signal.aborted) {
        console.error(error)
      }
    } finally {
      setAreViolationsLoading(false)
      //resule joyride tutorial if halted
      resumeJoyride()
    }
  }

  function onVinChange(input: string) {
    setFilters((pendingFilters) => ({ ...pendingFilters, vin: input }))
  }

  let onClickExport = undefined
  if (userHasPermission(['analyze', 'policy'])) {
    onClickExport = async function () {
      await api.offenses.exportOffenseList({
        include_abnormals: filters.include_abnormals,
        vin: filters.vin,
        start_date_before: filters.dateRange.to ? ZonedDateTimeToJSON(filters.dateRange.to) : undefined,
        end_date_after: filters.dateRange.from ? ZonedDateTimeToJSON(filters.dateRange.from) : undefined,
        event_types: filters.eventTypes,
        policy_ids: filters.policyId,
        policy_types: filters.policyKind,
        provider_ids: filters.provider,
        status: filters.status,
        offense_status: filters.offenseStatus,
        lang: i18n.language,
      })
    }
  }

  function onClickFilter(_event: React.MouseEvent<HTMLElement>): void {
    setFilters((pendingFilters) => ({ ...pendingFilters, include_abnormals: !pendingFilters.include_abnormals }))
  }

  function onApplyFilterClick() {
    if (!displayedFiltersRef.current) {
      throw new Error('There are no displayed filters')
    }

    setFilters(displayedFiltersRef.current)
  }

  useDeepCompareEffect(() => {
    const abortController = new AbortController()
    fetchViolations(abortController.signal)
    return () => abortController.abort()
  }, [filters])

  return (
    <FilteringCard
      SideComponent={
        <ViolationsListFilterPanel
          ref={displayedFiltersRef}
          initialFilters={filters}
          defaultFilters={defaultFilters}
          providers={activeProviders}
          policies={policies}
          violationsCount={violationsCount}
          violationsFacets={violationsFacets}
        />
      }
      onApplyFilter={onApplyFilterClick}
    >
      <ViolationsTable
        data={violations}
        isLoading={arePoliciesLoading || areViolationsLoading}
        totalCount={violationsCount}
        onClickExport={onClickExport}
        filter={{
          onClick: onClickFilter,
          title: (filters.include_abnormals
            ? t('common.exclude_abnormals_offenses')
            : t('common.include_abnormals_offenses')) as string,
          icon: filters.include_abnormals ? RemoveCircleOutlineIcon : AddCircleOutlineIcon,
        }}
        searchBar={{
          value: filters.vin,
          onChange: onVinChange,
          placeholder: t('pricing.vin'),
        }}
      />
    </FilteringCard>
  )
}

export default Violations
